import { ICommandBarItemProps, Icon, IconButton, Shimmer, ShimmerElementType } from "@fluentui/react";
import { OptionOnSelectData } from "@fluentui/react-components";
import { GridColDef } from "@mui/x-data-grid";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import DisplayAPIsException from "../../../BasicComponents/DisplayAPIsExceptionAlert";
import { logEvent, logException } from "../../../helper/AppInsightsHelper";
import { CustomShimmer } from "../../../helper/CustomShimmer";
import { ProductViewFiltersContext } from "../../../pages/ProductView";
import CustomCard from "../../../SharedComponents/CustomCard";
import { CustomDataGrid } from "../../../SharedComponents/CustomDataGrid/CustomDataGrid";
import { useGetTopTenantsOfCustomerMutation } from "../../../slices/ExploreProducts.slice";
import { ExportToExcel } from "../../../Utils/FileMenuFunctions";
import { OverviewAndSummaryMutationContext } from "../ProductView";
import CustomDropdown from "./CustomDropdown";
import { PRODUCT_VIEW_DATA } from "./ProductView.constants";
import { PRODUCT_VIEW_STYLES } from "./ProductView.styles";

const TopTenants = ({
  pageSizeOptions,
  paginationModel
}: {
  pageSizeOptions: number[];
  paginationModel: { page: number; pageSize: number };
}) => {
  const navigate = useNavigate();
  const {
    hasPartner,
    industry,
    isCAT,
    isFastTrackStrategic,
    isSST,
    productName,
    region,
    segmentGroup,
    segment,
    subSegment,
    isS500,
    isCCP,
    isTCT,
    majorGrowth
  } = useContext(ProductViewFiltersContext);
  const [paginationModelForTopImproving, setPaginationModelForTopImproving] = useState(paginationModel);
  const [paginationModelForTopDeclining, setPaginationModelForTopDeclining] = useState(paginationModel);
  const [selectedMetric, setSelectedMetric] = useState<string | undefined>("");
  const { data: overviewAndSummaryData } = useContext(OverviewAndSummaryMutationContext);
  const [getTopTenants, { data, error, isError, isLoading, isSuccess }] = useGetTopTenantsOfCustomerMutation({
    fixedCacheKey: "getTopTenants"
  });
  const cardShimmRef = useRef<any>();
  const selectedMetricRef = useRef(selectedMetric);
  const columns: GridColDef[] = [
    {
      headerName: "Tenant Name",
      field: "tenantName",
      minWidth: 265,
      flex: 1,
      sortable: true,
      renderCell: (params) => (
        <a
          style={{
            color: "rgb(0, 120, 212)",
            textDecoration: "none",
            fontSize: 12,
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}
          href="#"
          onClick={() => {
            navigate("/customer?id=" + params.row.tenantId + "&from=productview");
            // SetSelectedItem(params);
          }}
          title={params.row.tenantName}
        >
          {params.row.tenantName}
        </a>
      )
    },
    {
      headerName: "MoM Absolute",
      field: "moM",
      minWidth: 120,
      maxWidth: 200,
      flex: 1,
      sortable: true,
      renderCell: (params) => {
        const val = Number(parseFloat(params.row.moM).toFixed(2)).toLocaleString("en", {
          minimumFractionDigits: 0
        });
        return (
          <span
            style={{
              color: String(val).includes("-") ? "red" : "green"
            }}
          >
            {val}
          </span>
        );
      }
    },
    {
      headerName: "MoM%",
      field: "mom_Percent",
      minWidth: 100,
      maxWidth: 200,
      flex: 1,
      sortable: true,
      renderCell: (params) => {
        return (
          <span
            style={{
              color: String(params.row.mom_Percent).includes("-") ? "red" : "green"
            }}
          >
            {params.row.mom_Percent}%
          </span>
        );
      }
    },
    {
      headerName: "Current Value",
      field: "currentValue",
      minWidth: 120,
      maxWidth: 200,
      flex: 1,
      sortable: true,
      renderCell: (params) => (
        <>
          {Number(parseFloat(params.row.currentValue).toFixed(2)).toLocaleString("en", {
            minimumFractionDigits: 0
          })}
        </>
      )
    }
  ];

  const showNoGraphData = () => {
    const rootStyle = {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: "200px 0"
    };
    return <div style={rootStyle}>{`Table has no data to display by metric ${selectedMetric}`}</div>;
  };

  const renderCardHeader = (metricType: "Growing" | "Declining") =>
    isLoading ? (
      <Shimmer styles={PRODUCT_VIEW_STYLES.TAB.OPPORTUNITY_TENANTS.CSS_STYLES.cardHeaderShimmerStyles} />
    ) : (
      {
        title: data && data?.metricName && `Top ${metricType} Tenants for ${productName} by ${data?.metricName}`,
        tooltip:
          metricType === "Growing"
            ? PRODUCT_VIEW_DATA.TABS.TAB.OPPORTUNITY_TENANTS.CARD_HEADER.TOP_GROWING.tooltip
            : PRODUCT_VIEW_DATA.TABS.TAB.OPPORTUNITY_TENANTS.CARD_HEADER.TOP_DECLINING.tooltip
      }
    );

  const getExcelFormattedData = (excelData: any) =>
    excelData.map((x: any) => ({
      "Tenant Name": x.tenantName,
      "MoM Absolute": x.moM,
      "MoM %": Math.round(x.mom_Percent * 100) + "%",
      "Current Value": x.currentValue
    }));

  const topImprovingTenantsCardOverflowItems: ICommandBarItemProps[] = [
    {
      key: "exportToExcel",
      text: "Export to Excel",
      onRender: (item) => (
        <IconButton
          frameBorder={0}
          color="white"
          onClick={() =>
            ExportToExcel(
              getExcelFormattedData(data?.topImprovingTenants),
              "Security360_" + productName + "_TopGrowingTenants_by_" + data?.metricName
            )
          }
          style={{ display: "flex", alignItems: "center", width: 150 }}
        >
          <span style={{ marginLeft: 5 }}>
            <Icon iconName="Download" />
          </span>
          {item.text}
        </IconButton>
      )
    }
  ];

  const topDecliningTenantsCardOverflowItems: ICommandBarItemProps[] = [
    {
      key: "exportToExcel",
      text: "Export to Excel",
      onRender: (item) => (
        <IconButton
          frameBorder={0}
          color="white"
          onClick={() =>
            ExportToExcel(
              getExcelFormattedData(data?.topDecliningTenants),
              "Security360_" + productName + "_TopDecliningTenants_by_" + data?.metricName
            )
          }
          style={{ display: "flex", alignItems: "center", width: 150 }}
        >
          <span style={{ marginLeft: 5 }}>
            <Icon iconName="Download" />
          </span>
          {item.text}
        </IconButton>
      )
    }
  ];

  const handleSelectedMetrics = useCallback(({ optionText }: OptionOnSelectData) => {
    setSelectedMetric(optionText);
  }, []);

  const getChartTitle = (metricType: "Growing" | "Declining") => {
    return data && data?.metricName && `Top ${metricType} Tenants for ${productName} by ${data?.metricName} chart`;
  };

  const displayViewByMetricSelect = (isLoading && (
    <div style={{ flexBasis: "100%" }}>
      <Shimmer
        shimmerColors={{ shimmer: "var(--shimmer-color)" }}
        styles={{
          root: { paddingBottom: 10, width: "25%" },
          shimmerWrapper: { ".ms-ShimmerGap-root": { background: "var(--colorBodyBackground1)" } }
        }}
        shimmerElements={[
          { type: ShimmerElementType.line, width: "15%", height: 31.6 },
          { type: ShimmerElementType.gap, width: 10, height: 31.6 },
          { type: ShimmerElementType.line, width: "15%", height: 31.6 }
        ]}
      />
    </div>
  )) || (
      <CustomDropdown
        defaultOption={
          {
            optionText: data?.metricName,
            optionValue: data?.metricName,
            selectedOptions: data?.metricName ? [data?.metricName] : []
          } as OptionOnSelectData
        }
        onSelectedOption={handleSelectedMetrics}
        data={(overviewAndSummaryData?.overviewData || [])
          .filter(({ productKPIName }) => !productKPIName.includes("Tenants"))
          .map(({ productKPIName }) => ({ value: productKPIName, text: productKPIName }))}
        styles={{ root: { flexBasis: "100%", paddingBottom: 10 } }}
      />
    );

  useEffect(() => {
    selectedMetricRef.current = "";
  }, [productName]);

  useEffect(() => {
    selectedMetricRef.current = selectedMetric;
  }, [selectedMetric]);

  useEffect(() => {
    const apiBody = {
      SelectedTabName: "OpportunityTenants",
      HasPartner: hasPartner,
      Industry: industry,
      IsCAT: isCAT,
      IsFastTrackStrategic: isFastTrackStrategic,
      IsSST: isSST,
      ProductKPIName: selectedMetricRef.current,
      ProductName: productName,
      Region: region,
      SegmentGroup: segmentGroup,
      Segment: segment,
      SubSegment: subSegment,
      Vertical: "",
      isS500: isS500,
      isCCP: isCCP,
      isTCT: isTCT,
      majorGrowth: majorGrowth
    };
    getTopTenants(apiBody);
  }, [
    hasPartner,
    industry,
    isCAT,
    isFastTrackStrategic,
    isSST,
    productName,
    region,
    segmentGroup,
    segment,
    selectedMetric,
    subSegment,
    isS500,
    isCCP,
    isTCT,
    majorGrowth
  ]);

  useEffect(() => {
    if (isSuccess) {
      logEvent("getTopTenants API call success", "Product View Page", "TopTenants");
    }
    if (isError) {
      logException("Product View Page - Opportunity Tenants Tab", "TopTenants", "getTopTenants", error);
    }
  }, [isSuccess, isError]);

  const customProps = {
    productName: productName
  };

  useEffect(() => {
    logEvent("Opportunity Tenants", "Product View Page", "TopTenants", customProps);
  }, []);

  if (!isLoading && isError) {
    return <DisplayAPIsException error={error} height={cardShimmRef.current?.offsetHeight} />;
  }

  return (
    <div className={PRODUCT_VIEW_STYLES.TAB.OPPORTUNITY_TENANTS.CLASS_NAMES.cardsContainer}>
      {displayViewByMetricSelect}
      <div ref={cardShimmRef} className={PRODUCT_VIEW_STYLES.TAB.OPPORTUNITY_TENANTS.CLASS_NAMES.cardsContainerItem}>
        <CustomCard
          cardHeaderTitle={renderCardHeader("Growing")}
          styles={{
            ...PRODUCT_VIEW_STYLES.TAB.OPPORTUNITY_TENANTS.CSS_STYLES.cardStyles,
            root: { ...PRODUCT_VIEW_STYLES.TAB.OPPORTUNITY_TENANTS.CSS_STYLES.cardStyles.root, height: 530 },
            cardDetailsContent: isLoading ? { overflow: "hidden", padding: 13 } : { padding: 13, height: "100%" }
          }}
          overflowItems={topImprovingTenantsCardOverflowItems}
          chartTitle={getChartTitle("Growing")}
        >
          {isLoading ? (
            <CustomShimmer columnCount={columns.length} rowCount={13} />
          ) : (
            (data && data?.topImprovingTenants?.length > 0 && (
              <div
                className="carddata"
                style={{
                  height: "inherit", // Adjust height to account for padding
                  overflow: data?.topImprovingTenants?.length > 0 ? "auto" : "",
                  backgroundColor: "white"
                }}
              >
                <CustomDataGrid
                  columns={columns}
                  getRowId={(row) => row.tenantId}
                  pageSizeOptions={pageSizeOptions}
                  rows={{ values: data?.topImprovingTenants ?? [], count: data?.topImprovingTenants?.length ?? 0 }}
                  kustoLink={data?.improvingTenantsKustoLink}
                  paginationOptions={paginationModelForTopImproving}
                  paginationModel={paginationModelForTopImproving}
                  onPaginationModelChange={setPaginationModelForTopImproving}
                  disableColumnMenu
                  disableColumnFilter
                  disableRowSelectionOnClick
                  toolbarConfig={{
                    disableGridToolbarColumnsButton: true,
                    disableGridToolbarFilterButton: true,
                    disableGridToolbarExport: true,
                    disableGridToolbarQuickFilter: true
                  }}
                />
              </div>
            )) ||
            (data && data?.topImprovingTenants?.length === 0 && showNoGraphData()) ||
            (isError && <DisplayAPIsException height={cardShimmRef.current?.offsetHeight} />)
          )}
        </CustomCard>
      </div>
      <div className={PRODUCT_VIEW_STYLES.TAB.OPPORTUNITY_TENANTS.CLASS_NAMES.cardsContainerItem}>
        <CustomCard
          cardHeaderTitle={renderCardHeader("Declining")}
          styles={{
            ...PRODUCT_VIEW_STYLES.TAB.OPPORTUNITY_TENANTS.CSS_STYLES.cardStyles,
            root: { ...PRODUCT_VIEW_STYLES.TAB.OPPORTUNITY_TENANTS.CSS_STYLES.cardStyles.root, height: 530 },
            cardDetailsContent: isLoading ? { overflow: "hidden", padding: 13 } : { padding: 13, height: "100%" }
          }}
          overflowItems={topDecliningTenantsCardOverflowItems}
          chartTitle={getChartTitle("Declining")}
        >
          {isLoading ? (
            <CustomShimmer columnCount={columns.length} rowCount={13} />
          ) : (
            (data && data?.topDecliningTenants?.length > 0 && (
              <div
                className="carddata"
                style={{
                  height: "inherit", // Adjust height to account for padding
                  overflow: data?.topDecliningTenants?.length > 0 ? "auto" : "",
                  backgroundColor: "white"
                }}
              >
                <CustomDataGrid
                  columns={columns}
                  getRowId={(row) => row.tenantId}
                  pageSizeOptions={pageSizeOptions}
                  rows={{ values: data?.topDecliningTenants ?? [], count: data?.topDecliningTenants?.length ?? 0 }}
                  kustoLink={data?.decliningTenantsKustoLink}
                  paginationOptions={paginationModelForTopDeclining}
                  paginationModel={paginationModelForTopDeclining}
                  onPaginationModelChange={setPaginationModelForTopDeclining}
                  disableColumnMenu
                  disableColumnFilter
                  disableRowSelectionOnClick
                  toolbarConfig={{
                    disableGridToolbarColumnsButton: true,
                    disableGridToolbarFilterButton: true,
                    disableGridToolbarExport: true,
                    disableGridToolbarQuickFilter: true
                  }}
                />
              </div>
            )) ||
            (data && data?.topDecliningTenants?.length === 0 && showNoGraphData()) ||
            (isError && <DisplayAPIsException height={cardShimmRef.current?.offsetHeight} />)
          )}
        </CustomCard>
      </div>
    </div>
  );
};

export default TopTenants;
